/*
 * Разбор альбома планировок в браузере.
 *
 * Планы грузят с ноутбука, поэтому рендерим здесь: сервер остаётся без
 * системных пакетов и нативных сборок для разбора PDF — развёртывание
 * проекта хрупкое, и каждая новая системная зависимость на сервере — риск.
 * pdfjs-dist подключается динамическим импортом внутри функции: библиотека
 * весит мегабайты, и без этого приёма она осела бы в общем бандле и
 * утяжелила бы первый экран приложения, хотя нужна только на этом экране.
 *
 * Выход — всегда PNG: у чертежа тонкие линии, JPEG их размывает при сжатии,
 * а pdfkit на сервере вставляет PNG в протокол напрямую, без перекодировщиков.
 * Сервер принимает подложку исключительно в PNG и отвергает всё остальное.
 */

import type { PDFDocumentProxy } from 'pdfjs-dist';

/** Длинная сторона подложки: больше не нужно даже при сильном увеличении. */
const MAX_SIDE = 2400;

/** Длинная сторона миниатюры для выбора страницы — читать номер и общий вид
 *  чертежа, не печатать. Меньше MAX_SIDE на два порядка не просто по размеру
 *  файла: сама отрисовка канвой в pdf.js — самая дорогая часть разбора, и её
 *  стоимость растёт от разрешения, а не от того, сохраняем мы потом PNG или
 *  нет (см. renderPdfThumbs). */
const THUMB_SIDE = 320;

/**
 * Сколько ждать один шаг разбора (открытие документа воркером или разбор
 * одной страницы), прежде чем считать это зависанием. Лимит — на шаг, а не
 * на всю операцию: у альбома в 20 страниц общий бюджет в 20 секунд мешал бы,
 * а не помогал, а зависнуть воркер может не только на открытии, но и
 * посреди рендера конкретной (например, повреждённой) страницы.
 */
const STEP_TIMEOUT_MS = 20_000;

/** Гонка с таймаутом: без него сбой воркера в браузере выглядит как «кнопка
 *  ничего не делает» — счётчик busy крутится, а промис pdf.js так и не
 *  отклоняется сам по себе. */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/**
 * Выполняет body с воркером и гарантированно останавливает его по выходе —
 * успех, ошибка или таймаут внутри body, не важно. Нужно отдельно от
 * withTimeout: pdf.js получает воркер через workerPort как уже готовый
 * `Worker`-инстанс (см. renderPdfPages), а в этом случае его собственный
 * PDFWorker.destroy() реальный поток не останавливает — он останавливает
 * поле #webWorker, которое заполняется только при создании воркера самим
 * pdf.js изнутри (#initialize), а не при передаче готового порта
 * (#initializeFromPort). Значит порт, который создали мы сами, обязаны и
 * останавливать сами — иначе на каждый разбор PDF (новый этаж, новый альбом,
 * замена подложки) остаётся висеть ещё один нативный поток навсегда.
 */
export async function withWorker<W extends { terminate(): void }, T>(
  createWorker: () => W,
  body: (worker: W) => Promise<T>,
): Promise<T> {
  const worker = createWorker();
  try {
    return await body(worker);
  } finally {
    worker.terminate();
  }
}

export type PdfPage = { index: number; width: number; height: number; png: Blob; thumb: string };

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('не удалось получить PNG из canvas'))),
      'image/png',
    );
  });
}

function newCanvas(width: number, height: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d недоступен');
  return { canvas, ctx };
}

/** Открывает PDF в воркере, отдаёт документ в body и гарантированно освобождает
 *  и воркер, и сам документ по выходе — общий кусок для всех операций ниже
 *  (полный рендер, миниатюры, рендер одной страницы). */
async function withPdfDoc<T>(file: File, body: (doc: PDFDocumentProxy) => Promise<T>): Promise<T> {
  const pdfjs = await import('pdfjs-dist');
  // pdfjs-dist 6.x поставляет воркер как ES-модуль (pdf.worker.min.mjs, внутри
  // — import.meta). Раньше здесь был `?url` + workerSrc: Vite просто копировал
  // файл как есть, и браузер получал его адрес голой строкой — при попытке
  // запустить такой воркер классическим (без type: "module") падает
  // «Cannot use 'import.meta' outside a module». Суффикс ?worker — другое
  // дело: он заставляет Vite/Rollup по-настоящему СОБРАТЬ файл воркера, а не
  // скопировать, и сборка убирает import.meta вместе с остальным ESM-кодом.
  // На выходе — самодостаточный скрипт без внешних зависимостей от типа
  // воркера; отдаём его pdfjs уже готовым инстансом через workerPort, а не
  // строкой через workerSrc — так pdfjs не пытается сам пересобирать Worker
  // из адреса, формат которого ему неизвестен.
  const { default: PdfWorker } = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker');

  return withWorker(
    () => new PdfWorker(),
    async (worker) => {
      pdfjs.GlobalWorkerOptions.workerPort = worker;

      const task = pdfjs.getDocument({ data: await file.arrayBuffer() });

      try {
        const doc = await withTimeout(
          task.promise,
          STEP_TIMEOUT_MS,
          'PDF не открылся: истекло время ожидания воркера',
        );
        return await body(doc);
      } finally {
        // Гасим task при любом исходе — успех, ошибка разбора, таймаут на
        // открытии или на конкретной странице. worker.terminate() в
        // withWorker уже остановит сам поток; task.destroy() — это
        // отдельно, штатное освобождение ресурсов на стороне pdf.js
        // (декодированные шрифты, кэш страниц), если документ успел
        // открыться.
        task.destroy().catch(() => {});
      }
    },
  );
}

/** Рендерит одну страницу уже открытого документа в канву под нужную длинную сторону.
 *  Чертёж — вектор: рендерим сразу под нужное разрешение, а не растягиваем потом растр —
 *  так линии остаются чёткими. */
async function renderPageToCanvas(
  doc: PDFDocumentProxy,
  pageNum: number,
  side: number,
): Promise<HTMLCanvasElement> {
  const page = await doc.getPage(pageNum);
  const base = page.getViewport({ scale: 1 });
  const scale = side / Math.max(base.width, base.height);
  const viewport = page.getViewport({ scale });

  const { canvas } = newCanvas(Math.round(viewport.width), Math.round(viewport.height));
  await page.render({ canvas, viewport }).promise;
  return canvas;
}

/** Проверка числа страниц — до рендера, а не после: документ на 500 страниц иначе
 *  рендерился бы целиком, прежде чем отказ доходит до человека. */
function checkPageCount(numPages: number, maxPages?: number) {
  if (maxPages && numPages > maxPages) {
    throw new Error(`В документе больше ${maxPages} страниц — столько не поддерживается`);
  }
}

/** Рендерит страницы PDF в PNG. Длинная сторона — MAX_SIDE.
 *  maxPages — отвергнуть документ раньше рендера, а не после (см. вызов из Board.tsx: доска
 *  ограничена 200 страницами спекой, у альбома обходов такого предела нет — параметр необязателен).
 *  Каждая страница — полноразмерный PNG: подходит для альбома планировок (FloorPlans.tsx), где
 *  из разбора сразу импортируют отмеченные страницы, все сразу. Для одного лишь выбора страницы
 *  из большого документа (доска в звонке) это избыточно — там нужен renderPdfThumbs. */
export async function renderPdfPages(
  file: File,
  onProgress?: (done: number, total: number) => void,
  maxPages?: number,
): Promise<PdfPage[]> {
  return withPdfDoc(file, async (doc) => {
    checkPageCount(doc.numPages, maxPages);
    const pages: PdfPage[] = [];

    for (let i = 1; i <= doc.numPages; i++) {
      const pageNum = i;
      pages.push(
        await withTimeout(
          (async () => {
            const canvas = await renderPageToCanvas(doc, pageNum, MAX_SIDE);
            return {
              index: pageNum - 1,
              width: canvas.width,
              height: canvas.height,
              png: await canvasToPng(canvas),
              thumb: canvas.toDataURL('image/jpeg', 0.6),
            };
          })(),
          STEP_TIMEOUT_MS,
          `Страница ${pageNum}: истекло время ожидания воркера`,
        ),
      );
      onProgress?.(i, doc.numPages);
    }

    return pages;
  });
}

export type PdfPageThumb = { index: number; width: number; height: number; thumb: string };

/** Только миниатюры страниц (THUMB_SIDE), без полноразмерного PNG — для выбора одной
 *  страницы из документа (раздел 5 спеки доски). Рендерить все страницы альбома в
 *  100–200 листов сразу в 2400 точек ради того, чтобы человек выбрал одну, — минуты
 *  замирания и сотни мегабайт в памяти; после выбора полный размер делает
 *  renderPdfPage для одной-единственной страницы. */
export async function renderPdfThumbs(
  file: File,
  onProgress?: (done: number, total: number) => void,
  maxPages?: number,
): Promise<PdfPageThumb[]> {
  return withPdfDoc(file, async (doc) => {
    checkPageCount(doc.numPages, maxPages);
    const pages: PdfPageThumb[] = [];

    for (let i = 1; i <= doc.numPages; i++) {
      const pageNum = i;
      pages.push(
        await withTimeout(
          (async () => {
            const canvas = await renderPageToCanvas(doc, pageNum, THUMB_SIDE);
            return {
              index: pageNum - 1,
              width: canvas.width,
              height: canvas.height,
              thumb: canvas.toDataURL('image/jpeg', 0.6),
            };
          })(),
          STEP_TIMEOUT_MS,
          `Страница ${pageNum}: истекло время ожидания воркера`,
        ),
      );
      onProgress?.(i, doc.numPages);
    }

    return pages;
  });
}

/** Полноразмерный PNG ровно одной страницы — вызывается только для страницы,
 *  которую человек выбрал в списке миниатюр (renderPdfThumbs). */
export async function renderPdfPage(file: File, pageIndex: number): Promise<PdfPage> {
  return withPdfDoc(file, async (doc) => {
    const pageNum = pageIndex + 1;
    return withTimeout(
      (async () => {
        const canvas = await renderPageToCanvas(doc, pageNum, MAX_SIDE);
        return {
          index: pageIndex,
          width: canvas.width,
          height: canvas.height,
          png: await canvasToPng(canvas),
          thumb: canvas.toDataURL('image/jpeg', 0.6),
        };
      })(),
      STEP_TIMEOUT_MS,
      `Страница ${pageNum}: истекло время ожидания воркера`,
    );
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('не удалось прочитать картинку'));
    };
    img.src = url;
  });
}

/** Готовит обычную картинку (JPG/PNG) как подложку: тот же PNG на выходе. */
export async function imageToPlan(file: File): Promise<PdfPage> {
  const img = await loadImage(file);
  // Растр, в отличие от PDF, уже имеет свой предел резкости: увеличивать
  // сверх исходного размера смысла нет, только сжимаем, если фото крупнее подложки.
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);

  const { canvas, ctx } = newCanvas(width, height);
  ctx.drawImage(img, 0, 0, width, height);

  return {
    index: 0,
    width,
    height,
    png: await canvasToPng(canvas),
    thumb: canvas.toDataURL('image/jpeg', 0.6),
  };
}
